import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { TouchableOpacity } from "react-native";
import Animated from "react-native-reanimated";
import styled from "styled-components/native";
import { useTranslation } from "react-i18next";
import {
  useRequestHistoryData,
  useUnreadRequestCountData,
} from "@apis/requestHistory";
import { VDBText } from "@components/atoms";
import Loader from "@components/atoms/Loader";
import { SpaceType } from "@components/atoms/Spacer/types";
import FooterSpace from "@components/common/FooterSpace";
import useHideNavsOnScroll from "@navigations/utils/useHideNavsOnScroll";
import { useSelector } from "react-redux";
import EmptyView from "../EmptyView/EmptyView";
import { convertProductType } from "../utils/utils";
import ItemDataView from "./ItemData";
import { StockItemRequestClass } from "@src/types/HistoryRequest";
import { getLocalizationString } from "@components/molecules/RequestHistory/ItemList/utils";
import MessageContainer from "@components/molecules/MessageContainer";
import { VDBIcons } from "@utils/VDBIcons";
import { ButtonType } from "@components/atoms/Button/types";
import { useIsFocused, useNavigationState } from "@react-navigation/native";
import { RequestTypes } from "@scenes/requests/utils/utils";
import useScreenOptionsHooks from "../../../../hooks/useScreenOptionsHooks";

const ItemList = ({
  isReceivedRequest,
  selectedTab,
  requestedItem,
}: {
  isReceivedRequest: boolean;
  selectedTab: string;
  requestedItem?: any;
}) => {
  const { t } = useTranslation();
  const { theme, typography, user } = useScreenOptionsHooks();
  const [isUnread, setIsUnread] = useState(true);
  const [loadPagination, setLoadPagination] = useState(false);
  const notificationSelected = useSelector(
    (state: any) => state.appState?.notificationSelected
  );
  const isFocused = useIsFocused();
  const mState = useNavigationState((state) => state);
  const subState = mState?.routes?.[mState?.index] ?? null;
  const selectedItemId = subState?.params?.id ?? null;

  const [selectedItem, setSelectedItem] =
    useState<StockItemRequestClass | null>(null);
  const missingItemRefetchKeyRef = useRef<string | null>(null);

  const { scrollHandler } = useHideNavsOnScroll();

  const localizationString = useMemo(
    () => getLocalizationString(isReceivedRequest, selectedTab),
    [isReceivedRequest, selectedTab]
  );

  const requestHistoryData = useRequestHistoryData(
    localizationString.selection,
    !isUnread,
    user.currency,
    user.price_mode
  );
  const data: StockItemRequestClass[] = convertProductType(
    requestHistoryData.data?.pages
      ?.map((page) => page.data?.data?.stock_item_requests)
      .flat() || [],
    t
  );

  const requestUnreadHistoryData = useUnreadRequestCountData(
    isReceivedRequest ? "seller" : "buyer",
    localizationString.unReadSelection,
    isUnread,
    true,
    loadPagination
  );

  const unreadData: StockItemRequestClass[] = convertProductType(
    requestUnreadHistoryData.data?.pages
      ?.map((page) => page.data?.stock_item_request)
      .flat() || [],
    t
  );

  const unReadCount =
    requestUnreadHistoryData?.data?.pages[0]?.data?.count || 0;

  useEffect(() => {
    setIsUnread(false);
    setSelectedItem(null);
  }, [selectedTab]);

  useEffect(() => {
    if (selectedItemId) {
      const item = data.find(
        (_item) =>
          Number(_item?.id) === (selectedItemId ? Number(selectedItemId) : 0)
      );
      setSelectedItem(item ?? null);
    }
  }, [selectedItemId]);

  useEffect(() => {
    if (
      !selectedItemId ||
      isUnread ||
      requestHistoryData.isFetching ||
      !requestHistoryData.isFetched
    ) {
      return;
    }
    const isInList = data.some(
      (_item) => Number(_item?.id) === Number(selectedItemId)
    );
    const refetchKey = `${selectedItemId}:${localizationString.selection}`;
    if (!isInList && missingItemRefetchKeyRef.current !== refetchKey) {
      missingItemRefetchKeyRef.current = refetchKey;
      requestHistoryData.refetch();
    }
  }, [
    selectedItemId,
    data,
    isUnread,
    localizationString.selection,
    requestHistoryData.isFetching,
    requestHistoryData.isFetched,
  ]);

  useEffect(() => {
    if (
      unReadCount === 0 &&
      localizationString.selection === ("seller_unread" || "buyer_unread")
    ) {
      setIsUnread(false);
    }
  }, [selectedTab, unReadCount]);

  const handleLoadMore = () => {
    if (
      requestHistoryData.hasNextPage &&
      !requestHistoryData.isFetchingNextPage &&
      !isUnread
    ) {
      requestHistoryData.fetchNextPage().then();
    } else if (
      "hasNextPage" in requestUnreadHistoryData &&
      requestUnreadHistoryData.hasNextPage &&
      !requestUnreadHistoryData.isFetchingNextPage &&
      isUnread
    ) {
      requestUnreadHistoryData.fetchNextPage().then();
    }
  };

  const handleUnreadNotificationPress = () => {
    setIsUnread((old) => !old);
    setLoadPagination((old) => !old);
    setSelectedItem(null);
  };

  const handleItemPress = (item: StockItemRequestClass) => {
    setSelectedItem(item);
  };

  const onScrollHandler = scrollHandler();
  const onScroll = isFocused ? onScrollHandler : undefined;

  const renderHeader = () => (
    <UnReadContainer unReadCount={unReadCount} isUnread={isUnread}>
      <TouchableOpacity onPress={() => handleUnreadNotificationPress()}>
        <UnReadTextContainer>
          <VDBText type={typography["Body-M"]} color={theme["link-static"]}>
            {isUnread
              ? t("requestHistory.showAll.text")
              : `${t("requestHistory.unreadRequest.text", {
                  unReadCount,
                })}`}
          </VDBText>
        </UnReadTextContainer>
      </TouchableOpacity>
      {!theme.isLargeScreen && <LineView />}
    </UnReadContainer>
  );

  useEffect(() => {
    if (!notificationSelected) {
      setSelectedItem(null);
    }
  }, [notificationSelected]);

  const renderFooter = () => {
    return (
      <FooterSpace
        isLoader={
          requestHistoryData.isFetchingNextPage ||
          requestUnreadHistoryData.isFetchingNextPage
        }
        backgroundColor={"core-03"}
        extraHeight={160}
      />
    );
  };

  if (
    requestHistoryData.isLoading ||
    (isUnread &&
      (requestUnreadHistoryData.isLoading ||
        requestUnreadHistoryData.isRefetching))
  )
    return (
      <DataContainer>
        <Loader />
      </DataContainer>
    );
  if (requestHistoryData.isError)
    return (
      <DataContainer>
        <MessageContainer
          messageTitle={t("requestHistory.errorMessage.title.text")}
          message={t("requestHistory.errorMessage.description.text")}
          iconName={VDBIcons.WARNING}
          iconColor={theme["core-03"]}
          buttons={{
            type: ButtonType.primary,
            title: t("requestHistory.errorMessage.Reload.text"),
            onPressAction: () => requestHistoryData.refetch(),
            textTypographyStyle: "H3-M",
          }}
        />
      </DataContainer>
    );
  if (data.length === 0 && requestHistoryData.isSuccess)
    return (
      <DataContainer>
        <EmptyView
          emptyTitle={localizationString.emptyTitle}
          emptyDescription={localizationString.emptyDescription}
          showButton={!isReceivedRequest}
        />
      </DataContainer>
    );

  return (
    <MainContainer>
      <Animated.FlatList
        ListHeaderComponent={renderHeader}
        horizontal={false}
        contentContainerStyle={{
          paddingTop: !theme.isLargeScreen
            ? SpaceType.Basic
            : SpaceType.ExtraLarge,
        }}
        data={isUnread ? unreadData : data}
        renderItem={({ item }) => (
          <ItemDataView
            item={
              item.request_type === RequestTypes.Order
                ? {
                    ...item,
                    ...(item?.view_type === "seller_view"
                      ? { status: "received" }
                      : { status: "submitted" }),
                  }
                : item
            }
            isReceivedRequest={isReceivedRequest}
            selectedItem={selectedItem}
            onItemPress={handleItemPress}
            selectedTab={selectedTab}
            requestedItem={requestedItem}
            selectedItemId={selectedItemId}
          />
        )}
        keyExtractor={(item, index) => item.id.toString() + index}
        onScroll={onScroll}
        onEndReached={handleLoadMore}
        ListFooterComponent={renderFooter}
        onEndReachedThreshold={0.3}
        bounces={false}
        removeClippedSubviews
        contentInsetAdjustmentBehavior="automatic"
      />
    </MainContainer>
  );
};

const UnReadTextContainer = styled.View<any>`
  padding-left: ${({ theme }) =>
    theme.isTablet || theme.isLargeScreen ? SpaceType.Big : SpaceType.Double}px;
  padding-right: ${({ theme }) =>
    theme.isTablet || theme.isLargeScreen ? SpaceType.Big : SpaceType.Double}px;
  padding-bottom: ${SpaceType.Basic}px;
`;

const UnReadContainer = styled.View<{ unReadCount: number; isUnread: boolean }>`
  display: ${({ unReadCount, isUnread }) =>
    unReadCount === 0 && !isUnread ? "none" : "flex"};
`;

const LineView = styled.View`
  height: 1px;
  flex-direction: row;
  background-color: ${({ theme }) => theme["core-03"]};
  width: 100%;
  max-width: 100%;
  margin-bottom: ${({ theme }) =>
    theme.isMobile ? SpaceType.Basic : SpaceType.Double}px;
`;

const DataContainer = styled.View<any>`
  position: relative;
  flex: 1;
  background-color: ${({ theme }) =>
    theme?.isLargeScreen ? theme["core-01"] : theme["core-02"]};
`;

const MainContainer = styled.View<any>`
  flex: 1;
  background-color: ${({ theme }) =>
    theme?.isLargeScreen ? theme["core-01"] : theme["core-02"]};
`;

export default memo(ItemList, (prev, next) => {
  return (
    prev.selectedTab === next.selectedTab &&
    prev.isReceivedRequest === next.isReceivedRequest
  );
});
